import { Box, Button, CircularProgress, Divider, Grid, Hidden, IconButton, Skeleton, Theme, Typography, useTheme } from "@mui/material";
import { BlueEditIcon, DragAndDropIcon, InfoIconOutlined } from "../../../../assets/SvgIcons";
import { useHistory } from "react-router";
import { checkImageExists, getDailyFreqInstallmentsMsg } from "../../../../utils/utilityFunctions";
import { CustomeBlueDatePicker, CustomeBlueDropDown, CustomeNumberInput, FormCheckBox } from "../../transcation/FromInput";
import { InvestmentFundType } from "../../../../redux-store/types/api-types";
import { HoldingsTableData } from "../holdings/types";
import { FormikProps, useFormikContext } from '../../../../lib/formik/Formik';
import { useSelector } from "react-redux";
import { currencyCodeMap, STP_FIRST_ORDER_FLAG_LABEL } from "../../../../utils/constant";
import dayjs from "../../../../utils/dayjs";
import { handleDateValidation } from ".";
import { MpowerValidationType } from "../../types";
import { RootStateType } from "../../../../redux-store/reducers";
import MFCheckbox from "../../../../lib/formik/Checkbox";

export default function IncomingSchemeCard({
    setFromSchemeDialogOpen,
    schemeLoading,
    isLoading,
    id,
    handleChangeScheme,
    isSubmitting,
}: {
    setFromSchemeDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
    schemeLoading: boolean;
    isLoading: boolean;
    id: number;
    handleChangeScheme: () => void;
    isSubmitting: boolean;
}): JSX.Element {
    const { palette } = useTheme()
    const history = useHistory()
    const { pathname } = history.location

    const isSwitchScreen = pathname.split('/')[pathname.split('/').length - 1] === 'switch'

    const formik = useFormikContext() as FormikProps<any>
    const { values, setFieldValue } = formik
    const { fund } = values
    const {
        outgoingSchDetails,
        outgoingSchInvDetails,
        incomingSchInvDetails,
        switchTo,
    }: {
        outgoingSchInvDetails: InvestmentFundType;
        outgoingSchDetails: HoldingsTableData;
        incomingSchInvDetails: InvestmentFundType;
        switchTo: boolean;
    } = fund[id]

    const { locale, currencyCode, currencySymbol } = useSelector((store: RootStateType) => store.currencyInfo);

    const Daily = incomingSchInvDetails?.validations?.find((item) => item?.frequency === 'DAILY' && item.transactionType === 'STP')
    const Weekly = incomingSchInvDetails?.validations?.find((item) => item?.frequency === 'WEEKLY' && item.transactionType === 'STP')
    const WeeklyDays = incomingSchInvDetails?.validations?.find((item) => item?.frequency === 'WEEKLYDAYS' && item.transactionType === 'STP')
    const Monthly = incomingSchInvDetails?.validations?.find((item) => item?.frequency === 'MONTHLY' && item.transactionType === 'STP')
    const Quarterly = incomingSchInvDetails?.validations?.find((item) => item?.frequency === 'QUARTERLY' && item.transactionType === 'STP')
    const Yearly = incomingSchInvDetails?.validations?.find((item) => item?.frequency === 'YEARLY' && item.transactionType === 'STP')

    function getSTPMinInstallmentAmount(selectedFreq: string) {
        const outSchValArr = outgoingSchInvDetails?.validations?.filter((item: any) => item?.transactionType === 'STP' && item.frequency)

        const inSchValArr = incomingSchInvDetails?.validations?.filter((item: any) => item?.transactionType === 'STP' && item.frequency)

        const inSchValidation = inSchValArr?.find((item: any) => `${item?.frequency ?? ""}`.toUpperCase() === selectedFreq)

        const outSchValidation = outSchValArr?.find((item: any) => `${item?.frequency ?? ""}`.toUpperCase() === selectedFreq)

        const minInstallmentAmtInSch = Number(inSchValidation?.minimumInstallmentAmount ?? 0)

        const minInstallmentAmtOutSch = Number(outSchValidation?.minimumInstallmentAmountOut ?? 0)

        return Math.max(minInstallmentAmtInSch, minInstallmentAmtOutSch)
    }


    const stpMinAmount = (key = 'minimumInstallmentAmount' as keyof MpowerValidationType) => {

        if (fund?.[id]?.frequency === 'WEEKLY') {
            return Weekly?.[key]
        } else if (fund?.[id]?.frequency === 'MONTHLY') {
            return Monthly?.[key]
        } else if (fund?.[id]?.frequency === 'QUARTERLY') {
            return Quarterly?.[key]
        } else if (fund?.[id]?.frequency === 'YEARLY') {
            return Yearly?.[key]
        } else if (fund?.[id]?.frequency === 'DAILY') {
            return Daily?.[key]
        } else if (fund?.[id]?.frequency === 'WEEKLYDAYS') {
            return WeeklyDays?.[key]
        } else {
            return 0
        }
    }

    function getSwitchMinInstallmentAmount() {
        const outSchValidation = outgoingSchInvDetails?.validations?.find((item: any) => item?.transactionType === 'RED')

        const inSchValidation = incomingSchInvDetails?.validations?.find((item: any) => item?.transactionType === 'RED')

        const minInstallmentAmtInSch = Number(inSchValidation?.minimumInstallmentAmount ?? 0)

        const minInstallmentAmtOutSch = Number(outSchValidation?.minimumInstallmentAmountOut ?? 0)

        return Math.max(minInstallmentAmtInSch, minInstallmentAmtOutSch)
    }

    return (
        <>
            {!switchTo && (
                <>
                    <Divider />
                    <Typography
                        variant='h2'
                        color={palette.text.primary}
                        fontSize={{ sm: '18px', xs: '18px', md: '20px', lg: '20px' }}
                    >
                        {isSwitchScreen ? 'Switch' : 'Transfer'} To
                    </Typography>
                    <DragAndDropIcon style={{ margin: '0 auto' }} />
                    <Box>
                        <Typography
                            textAlign={'center'}
                            variant='body_500'
                            color={palette.text.primary}
                            fontSize={18}
                        >
                            Select a {isSwitchScreen ? "switch" : 'transfer'}-to scheme
                        </Typography>
                        <Typography
                            textAlign={'center'}
                            variant='body_500'
                            color={palette.text.secondary}
                        >
                            to input switch details
                        </Typography>
                    </Box>
                    <Button
                        variant='outlined'
                        sx={(theme: Theme) => ({
                            ...theme.typography.body_500,
                            color: theme.palette.primary[500],
                            border: `1px solid ${theme.palette.primary[500]}`,
                            borderRadius: '5px',
                            padding: '7px 35px',
                            gap: '10px',
                            width: "fit-content",
                        })}
                        onClick={() => setFromSchemeDialogOpen(true)}
                        disabled={isLoading}
                    >
                        Select Scheme
                    </Button>
                </>
            )}
            {switchTo && (
                <>
                    <Divider />
                    <Typography
                        variant='h2'
                        fontSize={{ sm: '18px', xs: '18px', md: '20px', lg: '20px' }}
                        color={palette.text.primary}
                    >
                        {isSwitchScreen ? 'Switch' : 'Transfer'} To
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexDirection: 'row',
                                gap: '15px',
                                width: '85%'
                            }}
                        >
                            {(schemeLoading || isLoading) ? (
                                <CircularProgress />
                            ) : (
                                <img
                                    src={
                                        checkImageExists(`${incomingSchInvDetails?.fundAmcId}`) ?
                                            `../../../../../images/amcIcons/${incomingSchInvDetails?.fundAmcId}.svg` : `../../../../../images/amcIcons/default.svg`
                                    }
                                    width={'50px'}
                                    height={'50px'}
                                    alt="Bank name"
                                    style={{ borderRadius: '8px 8px 8px 8px' }}
                                />
                            )}
                            <Box
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flexGrow: 1,
                                }}
                            >
                                {(schemeLoading || isLoading) ? (
                                    <Skeleton variant='text' animation="wave" width={200} />
                                ) : (
                                    <Typography variant='body_600' color="text.primary"
                                        sx={{
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            maxWidth: '100%',
                                        }}> {incomingSchInvDetails?.fundName || ''}</Typography>
                                )}
                                {(schemeLoading || isLoading) ? (
                                    <Skeleton variant='text' animation="wave" width={200} />
                                ) : (
                                    <Box
                                        display={'flex'}
                                        flexDirection={'row'}
                                        gap={'5px'}
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flexGrow: 1,
                                        }}
                                    >
                                        <Typography
                                            variant='body_500'
                                            color={palette.text.secondary}
                                            sx={{
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                maxWidth: '100%',
                                            }}
                                        >
                                            {incomingSchInvDetails?.fundType || ''}
                                        </Typography>
                                        <Typography
                                            variant='body_500'
                                            color={palette.text.secondary}
                                            sx={{
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                maxWidth: '100%',
                                            }}
                                        >
                                            • {incomingSchInvDetails?.fundCap || ''}
                                        </Typography>
                                        {(incomingSchInvDetails?.riskType !== "NULL") && (
                                            <Typography
                                                variant='body_500'
                                                color={palette.success[800]}
                                                sx={{
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    maxWidth: '100%',
                                                }}
                                            >
                                                • {incomingSchInvDetails?.riskType || ''}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                                {(schemeLoading || isLoading) ? (
                                    <Skeleton variant='text' animation="wave" width={200} />
                                ) : (
                                    <Box sx={{ marginTop: "3px" }}>
                                        <Typography
                                            variant="body_500"
                                            color="text.primary"
                                        >
                                            ISIN:{' '}
                                            <Typography
                                                component="span"
                                                variant="body_500"
                                                color="text.secondary"
                                            >
                                                {incomingSchInvDetails?.isinCode ?? ""}
                                            </Typography>
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        {(schemeLoading || isLoading) ? (
                            <Skeleton
                                variant='text'
                                animation="wave"
                                width={100}
                            />
                        ) : (
                            <IconButton
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                }}
                                onClick={() => (schemeLoading || isLoading) ? null : handleChangeScheme()}
                            >
                                <BlueEditIcon />
                                <Hidden only={'xs'}>
                                    <Typography variant='body_500' color={palette.primary[500]}>Change</Typography>
                                </Hidden>
                            </IconButton>
                        )}
                    </Box>
                    {isSwitchScreen ? (
                        <Grid container>
                            <Grid item xs={12} md={6} lg={6}>
                                <CustomeBlueDropDown
                                    isLoading={schemeLoading || isLoading}
                                    disabled={isSubmitting}
                                    label='Switch Value'
                                    name={`fund[${id}].switchValue`}
                                    list={[{ value: 'amount', label: 'Amount' }, { value: 'unit', label: 'Unit' }]}
                                    boxStyle={{
                                        borderTopRightRadius: '0px',
                                        borderBottomRightRadius: '0px',
                                        borderRight: `1px solid ${palette.text.secondary}b2`
                                    }}
                                    onChange={(event, value) => {
                                        setFieldValue(`fund[${id}].switchValue`, event.target.value);
                                        if (event.target.value === 'amount') {
                                            setFieldValue(`fund[${id}].entireBalance`, false)
                                        }
                                    }}
                                />
                                {(fund?.[id]?.switchValue === 'unit') && (
                                    <FormCheckBox
                                        name={`fund[${id}].entireBalance`}
                                        isLoading={schemeLoading || isLoading}
                                        disabled={isSubmitting}
                                        onChange={(event, value) => {
                                            setFieldValue(`fund[${id}].entireBalance`, value);
                                            if (value) {
                                                setFieldValue(`fund[${id}].installment`, fund?.[id]?.switchValue === "amount" ? Math.round(outgoingSchDetails?.availableUnits * outgoingSchDetails?.navValue) : outgoingSchDetails?.availableUnits)
                                            }
                                        }}
                                    />
                                )
                                }
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <CustomeNumberInput
                                    minlabel={
                                        `${fund?.[id]?.switchValue === "amount" ?
                                            `Min: ${(String.fromCharCode(parseInt(currencySymbol, 16)) + " " + getSwitchMinInstallmentAmount())}` :
                                            `Max: ${outgoingSchDetails?.availableUnits ?? 0}`}`
                                    }
                                    warningText={fund?.[id]?.switchValue === 'amount' ? 'amount' : 'units'}
                                    name={`fund[${id}].installment`}
                                    label=''
                                    isLoading={schemeLoading || isLoading}
                                    infoAlign='right'
                                    removeIcons={fund?.[id]?.switchValue === 'unit'}
                                    textType='number'
                                    disable={fund?.[id]?.entireBalance || isSubmitting}
                                    boxStyle={{
                                        borderTopLeftRadius: '0px',
                                        borderBottomLeftRadius: '0px',
                                        marginTop: '3px'
                                    }}
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container spacing={2} rowGap={2}>
                            <Grid item xs={12} md={6} lg={6}>
                                <CustomeNumberInput
                                    textType='number'
                                    name={`fund[${id}].amount`}
                                    label="Transfer Amount"
                                    isLoading={schemeLoading || isLoading}
                                    disable={isSubmitting}
                                    minlabel={`Min:${String.fromCharCode(parseInt(currencySymbol, 16))} ${getSTPMinInstallmentAmount(fund?.[id]?.frequency ?? "")}`}
                                    warningText={`${currencyCodeMap[currencyCode?.toLowerCase()]} Five Thousand`}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <CustomeBlueDropDown
                                    name={`fund[${id}].frequency`}
                                    list={incomingSchInvDetails?.frequencies?.filter(item => item.tranasactionType === 'STP' && item.frequency).map(item => ({ value: item.frequency ?? '', label: item.frequency ?? '' })) || []}
                                    isLoading={schemeLoading || isLoading}
                                    disabled={isSubmitting}
                                    label="STP Frequency" />
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <CustomeBlueDatePicker
                                    name={`fund[${id}].swpDate`}
                                    isLoading={schemeLoading || isLoading}
                                    disabled={isSubmitting}
                                    disablePast
                                    label={(fund?.[id]?.frequency === 'DAILY' && !isSwitchScreen) ? "STP Start Date" : "STP Date"}
                                    shouldDisableDate={(date: dayjs.Dayjs) => handleDateValidation({
                                        ArrayOfDate: stpMinAmount('investmentDates'),
                                        date,
                                        weekDaysAllowed: fund?.[id]?.incomingSchWeekDays?.STP_WEEKDAYS ?? [],
                                        frequency: fund?.[id]?.frequency,
                                        isStp: true
                                    })}
                                    // For T+1 date
                                    minDate={dayjs().add(1, 'day')}
                                />
                            </Grid>
                            {(fund?.[id]?.frequency !== 'DAILY' && !isSwitchScreen) && (
                                <Grid item xs={12} md={6} lg={6}>
                                    <CustomeNumberInput
                                        name={`fund[${id}].installment`}
                                        textType='number'
                                        removeIcons
                                        disable={fund?.[id]?.frequency === 'DAILY' && !isSwitchScreen || isSubmitting}
                                        isLoading={schemeLoading || isLoading}
                                        label="No. of Installments"
                                        minlabel={`Min: ${(fund?.[id]?.frequency === 'DAILY' && !isSwitchScreen) ? '0' : stpMinAmount('minimumInstallments')}`}
                                    />
                                </Grid>)}
                            {(fund?.[id]?.frequency === 'DAILY' && !isSwitchScreen) && (
                                <Grid item xs={12} md={6} lg={6}>
                                    <CustomeBlueDatePicker
                                        name={`fund[${id}].stpEndDate`}
                                        isLoading={schemeLoading || isLoading}
                                        disablePast
                                        disabled={fund?.[id]?.swpDate === '' || isSubmitting}
                                        label="STP End Date"
                                        shouldDisableDate={(date: dayjs.Dayjs) => dayjs(date).isBefore(dayjs(fund?.[id]?.swpDate))}
                                    />
                                </Grid>
                            )}
                            {(!isLoading && !schemeLoading && (fund?.[id]?.frequency === 'DAILY')) && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        columnGap: "5px",
                                        marginLeft: "16px"
                                    }}>
                                    <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                                        <InfoIconOutlined />
                                    </Box>
                                    <Typography
                                        variant='subtitle_600'
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.grey[500]
                                        })}
                                    >
                                        {getDailyFreqInstallmentsMsg(Daily?.minimumInstallments ?? 0)}
                                    </Typography>
                                </Box>
                            )}
                            <Grid item xs={12} md={6}>
                                {(schemeLoading || isLoading) && (
                                    <Skeleton
                                        animation="wave"
                                        width="100%"
                                        sx={{
                                            mt: 1,
                                            padding: '30px',
                                            borderRadius: 2,
                                        }}
                                    />
                                )}
                                {!(schemeLoading || isLoading) && (
                                    <Box
                                        sx={{
                                            mt: 3,
                                            padding: '4px 0px 4px 15px',
                                            backgroundColor: palette.primary[50],
                                            borderRadius: 2
                                        }}
                                    >
                                        <MFCheckbox
                                            name={`fund[${id}].firstOrder`}
                                            label={STP_FIRST_ORDER_FLAG_LABEL}
                                            disabled={isSubmitting}
                                        />
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    )
                    }
                </>
            )}
        </>)
}